import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, ClockCounterClockwise, MagnifyingGlass, Eye } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_TYPES = [
  { value: '__all__', label: 'All entities' },
  { value: 'payment_voucher', label: 'Payment Voucher' },
  { value: 'receipt_voucher', label: 'Receipt Voucher' },
  { value: 'journal_entry', label: 'Journal Entry' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'purchase_rfq', label: 'RFQ' },
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'grn', label: 'GRN' },
  { value: 'subcontractor_agreement', label: 'Subcontractor Agreement' },
  { value: 'agreement', label: 'Agreement (Legacy)' },
  { value: 'company', label: 'Company Profile' },
  { value: 'period_lock', label: 'Period Lock' },
  { value: 'user', label: 'User' },
  { value: 'account', label: 'Chart of Accounts' },
];

const ACTION_COLORS = {
  create: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
  update: 'bg-blue-500/10 text-blue-700 border-blue-200',
  delete: 'bg-red-500/10 text-red-700 border-red-200',
  approve: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
  reject: 'bg-red-500/10 text-red-700 border-red-200',
  void: 'bg-amber-500/10 text-amber-700 border-amber-200',
  login: 'bg-slate-500/10 text-slate-700 border-slate-200',
};

export default function AuditLogPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [entityType, setEntityType] = useState('__all__');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const token = localStorage.getItem('erp_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (entityType && entityType !== '__all__') params.append('entity_type', entityType);
      if (search) params.append('search', search);
      if (fromDate) params.append('start_date', fromDate);
      if (toDate) params.append('end_date', toDate + 'T23:59:59');
      params.append('limit', '200');
      const res = await axios.get(`${API_URL}/api/audit/logs?${params}`, { headers });
      setLogs(res.data?.logs || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, search, fromDate, toDate]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-8 p-6 lg:p-8" data-testid="audit-log-page">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2" data-testid="audit-back">
          <ArrowLeft size={14} className="mr-1" /> Back
        </Button>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold flex items-center gap-2">
          <ClockCounterClockwise size={32} className="text-primary" /> Audit Trail
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Complete history of every change · {total} event{total === 1 ? '' : 's'} on file
        </p>
      </motion.div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2">
              <Label>Search</Label>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="user, action, or detail keyword…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="audit-search"
                />
              </div>
            </div>
            <div>
              <Label>Entity</Label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger data-testid="audit-entity-select"><SelectValue placeholder="All entities" /></SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="audit-from" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="audit-to" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Detail</TableHead>
                <TableHead className="text-right">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Loading…</TableCell></TableRow>
              )}
              {!loading && logs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No audit events match your filters.</TableCell></TableRow>
              )}
              {!loading && logs.map((log) => (
                <TableRow key={log.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => setSelected(log)} data-testid={`audit-row-${log.id}`}>
                  <TableCell className="font-mono text-xs whitespace-nowrap">
                    {(log.timestamp || '').replace('T', ' ').slice(0, 19)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={ACTION_COLORS[log.action] || ''}>
                      {log.action_description || log.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{log.entity_type || log.document_type || '—'}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {log.document_number || (log.entity_id || log.document_id || '').slice(0, 8)}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{log.user_email || log.user_name || '—'}</TableCell>
                  <TableCell className="text-xs max-w-md truncate">
                    {(() => {
                      // Surface the most useful field(s) inline. Priority:
                      // 1) explicit reason (deletion/edit/rejection/termination)
                      // 2) prior status transition
                      // 3) fallback to details JSON summary
                      const reason = log.deletion_reason || log.edit_reason ||
                        log.rejection_reason || log.termination_reason || log.reason;
                      if (reason) return <span className="text-amber-700 font-medium">Reason: {reason}</span>;
                      if (log.prior_status) return <span>Prior status: <span className="font-medium">{log.prior_status}</span></span>;
                      if (log.details) {
                        return <span className="text-muted-foreground">{Object.entries(log.details).slice(0, 3).map(([k, v]) => `${k}=${v}`).join(' · ')}</span>;
                      }
                      return <span className="text-muted-foreground">—</span>;
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelected(log); }}>
                      <Eye size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Event Detail</DialogTitle>
            <DialogDescription>
              {selected?.timestamp?.replace('T', ' ').slice(0, 19)} · {selected?.user_email}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-xs uppercase text-muted-foreground">Action</div><div className="font-medium">{selected.action_description || selected.action}</div></div>
                <div><div className="text-xs uppercase text-muted-foreground">Entity</div><div className="font-medium">{selected.entity_type || selected.document_type}</div></div>
                <div><div className="text-xs uppercase text-muted-foreground">Document #</div><div className="font-mono text-xs">{selected.document_number || selected.entity_id || selected.document_id}</div></div>
                <div><div className="text-xs uppercase text-muted-foreground">By</div><div className="text-xs">{selected.user_name || selected.user_email || '—'}</div></div>
                {selected.prior_status && (
                  <div><div className="text-xs uppercase text-muted-foreground">Prior Status</div><div className="font-medium">{selected.prior_status}</div></div>
                )}
                {selected.ip_address && (
                  <div><div className="text-xs uppercase text-muted-foreground">IP Address</div><div className="text-xs">{selected.ip_address}</div></div>
                )}
              </div>

              {/* Prominent Reason block — the whole point of the audit trail */}
              {(() => {
                const reason = selected.deletion_reason || selected.edit_reason ||
                  selected.rejection_reason || selected.termination_reason || selected.reason;
                if (!reason) return null;
                const label = selected.deletion_reason ? 'Deletion Reason'
                  : selected.edit_reason ? 'Edit Reason'
                  : selected.rejection_reason ? 'Rejection Reason'
                  : selected.termination_reason ? 'Termination Reason'
                  : 'Reason';
                return (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900/40 p-3">
                    <div className="text-xs uppercase text-amber-800 dark:text-amber-300 font-semibold tracking-wide mb-1">{label}</div>
                    <div className="text-sm text-amber-900 dark:text-amber-100 whitespace-pre-wrap">{reason}</div>
                  </div>
                );
              })()}

              <div>
                <div className="text-xs uppercase text-muted-foreground mb-1">Raw Details</div>
                <pre className="text-xs bg-muted/40 p-3 rounded overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(selected.details || selected, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSelected(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
